import { type ReactNode, useEffect, useRef, useState } from "react";
import { Avatar } from "@/components/app/avatar";
import { SubTabs } from "@/components/app/layout";
import { Card } from "@/components/ui/card";
import { useTheme } from "@/lib/ground";
import { kitEdge, readableOn } from "@/lib/team-color";
import { ADM_QUERY, useMediaQuery } from "@/lib/useMediaQuery";
import { cn } from "@/lib/utils";
import type { RosterProfileLink } from "../../../shared/player-profile";
import { groupStake } from "../../../shared/standings";
import {
  breakCount,
  currentRun,
  type Division,
  formatClock,
  type GameState,
  type GameTeam,
  isBreak,
  longestPointMs,
  type Player,
  type PlayerTally,
  type PointEvent,
  type Pool,
  pointLengthMs,
  type ScheduledGame,
  type Team,
  tallyPlayers,
} from "../../../shared/types";
import { displayPercent, winProbabilityA } from "../../../shared/winprob";
import { useT } from "../../i18n";
import { ordinal } from "../../i18n/format";

/** The recorded game story: point tiles, player credits and existing derived
 * stats. Phones show one detailed view at a time; desktops keep credits and
 * leaders alongside each other. Missing tracking never invents a timeline. */
export function LiveStory({
  state,
  teams,
  schedule,
  pools,
  divisions,
  profiles,
}: {
  state: GameState;
  teams: Team[];
  schedule: ScheduledGame[];
  pools: Pool[];
  divisions: Division[];
  profiles?: ReadonlyMap<string, RosterProfileLink> | undefined;
}) {
  const t = useT();
  const wide = useMediaQuery(ADM_QUERY);
  const points = state.points ?? [];
  const sides = leadersOf(state, points);
  // The first recorded goal already has a scorer worth showing.
  const hasRun = points.length > 0;
  // "8-6 KYV" three times over is not commentary, it is the score with extra
  // steps: the list waits for somebody to be credited.
  const hasPoints = points.some((p) => p.scorerId !== null);
  const hasLeaders = sides.length > 0;

  const views = [
    ...(hasRun ? [{ id: "run" as const, label: t("board.runOfPlay") }] : []),
    ...(hasPoints ? [{ id: "points" as const, label: t("board.live.tab.points") }] : []),
    ...(hasLeaders ? [{ id: "leaders" as const, label: t("board.live.tab.leaders") }] : []),
  ];
  const [picked, setPicked] = useState<"run" | "points" | "leaders">("run");
  const active = views.some((v) => v.id === picked) ? picked : views[0]?.id;

  const facts = footOf(state, teams, schedule, pools, divisions, t);
  // The win model, which came down from the hero (2026-09-19): it is the one
  // MODELLED number on the screen, and this is where the derived ones live.
  const odds = state.period === "final" ? null : <WinBar state={state} />;
  // Nothing tracked, nothing at stake and no model: no pane, rather than an
  // empty one holding a gap open under the hero.
  if (views.length === 0 && facts === null && odds === null) return null;
  const foot = facts === null && odds === null ? null : <Foot facts={facts} odds={odds} />;
  const run = hasRun ? <RunOfPlay state={state} points={points} profiles={profiles} /> : null;
  const feed = hasPoints ? <Feed state={state} points={points} /> : null;
  const leaders = hasLeaders ? <Leaders sides={sides} /> : null;

  if (wide) {
    return (
      <Blocks>
        {run}
        {(feed !== null || leaders !== null) && (
          <div className={cn("grid", feed !== null && leaders !== null && "grid-cols-2 divide-x")}>
            {feed}
            {leaders}
          </div>
        )}
        {foot}
      </Blocks>
    );
  }
  return (
    <Blocks>
      {views.length > 1 && active !== undefined && (
        // THE TAB ROW IS THE PANE'S FIRST BAND, not a trough floating above
        // it. Above it there was nothing between the two: the trough is
        // `w-full` at 7% white over a card at 5.5%, the same plate twice and
        // touching, because `mb-0` on a direct child of a `space-y-*` wrapper
        // also deletes the WRAPPER's gap - Tailwind v4 writes `space-y` as a
        // zero-specificity `:where()` rule setting `margin-block-end` on the
        // child, which any `mb-*` class outranks. Inside the pane the band's
        // `px-4 py-3` is the rhythm every other block here uses, and the next
        // block's own `border-t` is the seam.
        // The row's three words still share the width, the account page's
        // segmented shape: a tab strip that hugs the left reads as a filter.
        <div className="px-4 py-3">
          <SubTabs
            views={views}
            active={active}
            onSelect={setPicked}
            className="mb-0"
            listClassName="flex-nowrap [&>[data-slot=tabs-trigger]]:flex-1"
          />
        </div>
      )}
      {active === "run" && run}
      {active === "points" && feed}
      {active === "leaders" && leaders}
      {foot}
    </Blocks>
  );
}

/** The pane, or nothing when every block removed itself. */
function Blocks({ children }: { children: ReactNode }) {
  return (
    <Card className="gap-0 overflow-hidden p-0 empty:hidden [&>*]:border-t [&>*:first-child]:border-t-0">
      {children}
    </Card>
  );
}

/** Each tile shows the score reached and the team that scored. Hover, focus
 * or tap selects its detail; selecting the newest tile resumes following
 * incoming points. Browsing earlier points never gets displaced by a score. */
function RunOfPlay({
  state,
  points,
  profiles,
}: {
  state: GameState;
  points: PointEvent[];
  profiles: ReadonlyMap<string, RosterProfileLink> | undefined;
}) {
  const t = useT();
  const theme = useTheme();
  const [active, setActive] = useState<number | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  /** The reader is at the live end. Cleared the moment they scroll back, so a
   *  point landing mid-read cannot yank the strip out from under them. */
  const atLive = useRef(true);

  // Blocks have a floor width, so a long game OVERFLOWS instead of slicing
  // itself into slivers. New points arrive on the right, which is where the
  // strip parks itself.
  const seen = useRef(0);
  useEffect(() => {
    const el = strip.current;
    if (el === null) return;
    // Only on a NEW point. The pitch re-renders for its clock, and yanking the
    // scroll on every one of those would make the strip impossible to read
    // back through.
    if (points.length === seen.current) return;
    seen.current = points.length;
    if (!atLive.current) return;
    el.scrollLeft = el.scrollWidth;
  }, [points.length]);

  const run = currentRun(points);
  const runTeam = run === null ? null : run.team === "A" ? state.teamA : state.teamB;
  // Falls back rather than clamps: a point landing mid-hover must not silently
  // re-point the detail line at a different point than the one under the finger.
  const shownAt = active !== null && active < points.length ? active : points.length - 1;
  const shown = points[shownAt];

  return (
    <div className="px-4 py-3">
      {/* Run announcements replace the legend without moving the timeline. */}
      <div className="mb-2 flex min-h-5 items-center justify-between gap-3 text-muted-foreground">
        <span className="text-sm font-semibold text-foreground">{t("board.runOfPlay")}</span>
        {run !== null && run.length >= 3 && runTeam !== null ? (
          <span className="eyebrow text-brand-ink dark:text-brand">
            {t("board.onARun", { team: runTeam.short, n: run.length })}
          </span>
        ) : (
          points.some(isBreak) && (
            <span className="flex items-center gap-1.5 text-xs">
              <span aria-hidden="true" className="lean h-1 w-3.5 bg-brand" />
              {t("board.live.breakKey")}
            </span>
          )
        )}
      </div>

      <div
        ref={strip}
        // Cards keep a readable score and a thumb-sized target; long games
        // scroll within the strip instead of shrinking the point history.
        className="no-bar flex items-stretch gap-1.5 overflow-x-auto py-1"
        onScroll={(e) => {
          const el = e.currentTarget;
          atLive.current = el.scrollWidth - el.clientWidth - el.scrollLeft < 16;
        }}
      >
        {points.map((p, i) => {
          const team = p.team === "A" ? state.teamA : state.teamB;
          return (
            <button
              type="button"
              key={`${p.scoreA}-${p.scoreB}`}
              className={cn(
                "relative flex min-w-14 flex-1 cursor-pointer flex-col items-center gap-1 overflow-hidden rounded-lg border bg-muted/40 px-2 pt-3 pb-2 text-foreground transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                i === shownAt
                  ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                  : "border-transparent",
              )}
              aria-pressed={i === shownAt}
              onMouseEnter={() => setActive(i === points.length - 1 ? null : i)}
              onFocus={() => setActive(i === points.length - 1 ? null : i)}
              onClick={() => setActive(i === points.length - 1 ? null : i)}
              aria-label={`${p.scoreA}–${p.scoreB} ${team.short}`}
            >
              <span
                aria-hidden="true"
                data-edge={kitEdge(team.color, theme)}
                className="kit-edge absolute inset-x-0 top-0 h-1"
                style={{ background: readableOn(team.color).bg }}
              />
              <span
                aria-hidden="true"
                className="nxt whitespace-nowrap text-lg tabular-nums leading-none"
              >
                {p.scoreA}–{p.scoreB}
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  "team-code text-xs",
                  i === shownAt ? "text-primary-foreground/70" : "text-muted-foreground",
                )}
              >
                {team.short}
              </span>
              {isBreak(p) && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 h-0.5 w-4 rounded-full bg-brand"
                />
              )}
            </button>
          );
        })}
      </div>

      {shown !== undefined && <SelectedPoint state={state} point={shown} profiles={profiles} />}
    </div>
  );
}

/** The selected point gets one compact portrait row. Only verified public
 * profiles supply photos; uncredited points keep the scoring team's name. */
function SelectedPoint({
  state,
  point,
  profiles,
}: {
  state: GameState;
  point: PointEvent;
  profiles: ReadonlyMap<string, RosterProfileLink> | undefined;
}) {
  const t = useT();
  const team = point.team === "A" ? state.teamA : state.teamB;
  const scorer = team.roster?.find((p) => p.id === point.scorerId);
  const assist = team.roster?.find((p) => p.id === point.assistId);
  const profile = scorer ? profiles?.get(scorer.id) : undefined;
  return (
    <div className="mt-3 flex items-center gap-3">
      {scorer ? (
        <Avatar
          label={scorer.name}
          src={profile?.photo}
          focus={profile?.photoFocus}
          className="size-12 rounded-lg text-sm"
          photoClassName="size-12 aspect-square rounded-lg"
        />
      ) : (
        <MiniChip team={team} />
      )}
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2 text-[0.625rem] font-semibold uppercase tracking-wide text-muted-foreground">
          <span>{t("board.live.goal")}</span>
          {scorer && <span className="team-code">{team.short}</span>}
          {isBreak(point) && (
            <span className="text-brand-ink dark:text-brand">{t("board.break")}</span>
          )}
        </div>
        <p className="text-sm font-semibold [overflow-wrap:anywhere]">
          {profile && scorer ? (
            <a
              href={`/players/${encodeURIComponent(profile.accountId)}`}
              className="underline-offset-4 hover:underline"
            >
              {named(scorer)}
            </a>
          ) : scorer ? (
            named(scorer)
          ) : (
            team.name
          )}
        </p>
        {assist && (
          <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
            {t("board.live.assistBy", { player: named(assist) })}
          </p>
        )}
      </div>
      <span className="nxt tabular shrink-0 text-xl">
        {point.scoreA}–{point.scoreB}
      </span>
    </div>
  );
}

/**
 * One point, spelled out: the score it made, who made it, whether it broke.
 *
 * Shared by the strip's scrubber and the last-points list, so the two can never
 * describe the same point in two different ways. The assist drops to its own
 * line: «#12 Mykola Rievtov ← #2 Dmytrii Dokuchaiev» wrapped raggedly mid-name
 * at 375, and two short lines scan where one broken one does not.
 */
function PointRow({
  state,
  point,
  inline = false,
}: {
  state: GameState;
  point: PointEvent;
  /** One line, for the strip's heading row on a laptop, where the width is. */
  inline?: boolean;
}) {
  const t = useT();
  const team = point.team === "A" ? state.teamA : state.teamB;
  const find = (id: string | null): Player | null =>
    id === null ? null : ((team.roster ?? []).find((p) => p.id === id) ?? null);
  const scorer = find(point.scorerId);
  const assist = find(point.assistId);
  const broke = isBreak(point);

  return (
    <div
      className={cn(
        "text-sm",
        inline
          ? "flex items-center gap-2.5"
          : // A row of the CALLER'S grid (score, code, name, break), never its
            // own flex row: a four-letter code on one point and a two-letter
            // one on the next sized their own boxes and staggered the names by
            // 16px down the list (ODES 60px against SF's 44px floor).
            "col-span-4 grid grid-cols-subgrid items-start",
      )}
    >
      {/* No `w-8` with it: «10–12» measures 43px at this size and used to
          overflow a 32px box into the chip. The track sizes itself, and every
          row of one list shares it. */}
      <span className="tabular shrink-0 text-muted-foreground">
        {point.scoreA}–{point.scoreB}
      </span>
      <MiniChip team={team} />
      <span
        className={cn(
          "min-w-0",
          inline
            ? "flex-1 truncate"
            : // With no break chip the name takes that column too, or every
              // break-less row would be 60px narrower than its neighbours.
              cn("text-pretty", !broke && "col-span-2"),
        )}
      >
        {scorer === null ? (
          <span className="text-muted-foreground">{team.name}</span>
        ) : (
          <>
            {named(scorer)}
            {assist !== null && (
              <span className={cn("text-muted-foreground", !inline && "block")}>
                {inline && " "}← {named(assist)}
              </span>
            )}
          </>
        )}
      </span>
      {broke && (
        // Tighter than `.eyebrow` for the reason the chips under the story
        // are: at 0.3em the word took 70px of a phone's row from the name.
        <span className="slant-chip shrink-0 self-start bg-brand px-1.5 py-1 text-[0.5625rem] leading-none font-extrabold tracking-[0.16em] text-brand-on uppercase">
          {t("board.break")}
        </span>
      )}
    </div>
  );
}

function named(p: Player): string {
  return `${p.number !== "" ? `#${p.number} ` : ""}${p.name}`;
}

/** The team's code at a list row's size, on its own colour. */
function MiniChip({ team }: { team: GameTeam }) {
  const ink = readableOn(team.color);
  const theme = useTheme();
  return (
    <span
      data-edge={kitEdge(team.color, theme)}
      className="slant-chip team-code min-w-11 shrink-0 px-2 py-0.5 text-center text-xs leading-none"
      style={{ background: ink.bg, color: ink.fg }}
    >
      {team.short}
    </span>
  );
}

/** The last few points, newest first: play-by-play. Six, not the card's three:
 *  the list has a tab (or a column) of its own now. */
function Feed({ state, points }: { state: GameState; points: PointEvent[] }) {
  const t = useT();
  return (
    <div className="px-4 py-3">
      <div className="eyebrow mb-2.5 text-muted-foreground">{t("board.lastPoints")}</div>
      {/* The rows' shared tracks: the score, the team's code, the name, the
          break chip. Each `PointRow` is a `grid-cols-subgrid` row over them,
          so one grid sizes the code column for every row at once. */}
      <div className="grid grid-cols-[auto_auto_minmax(0,1fr)_auto] gap-x-2.5 gap-y-2">
        {points
          .slice(-6)
          .reverse()
          .map((p) => (
            <PointRow key={`${p.scoreA}-${p.scoreB}`} state={state} point={p} />
          ))}
      </div>
    </div>
  );
}

interface LeaderSide {
  side: "A" | "B";
  team: GameTeam;
  players: { tally: PlayerTally; player: Player }[];
}

/** Who has been doing the scoring, top three a side. NOT the last-points list:
 *  that is what just happened, this is who has been doing it all game. */
function leadersOf(state: GameState, points: PointEvent[]): LeaderSide[] {
  return (["A", "B"] as const)
    .map((side) => {
      const team = side === "A" ? state.teamA : state.teamB;
      return {
        side,
        team,
        players: tallyPlayers(points, side)
          .slice(0, 3)
          .map((tally) => ({
            tally,
            player: (team.roster ?? []).find((p) => p.id === tally.playerId),
          }))
          .filter((r): r is { tally: PlayerTally; player: Player } => r.player !== undefined),
      };
    })
    .filter((l) => l.players.length > 0);
}

/** A player a line, the team's code as the row's label: two names joined by a
 *  dot wrapped into a paragraph on a phone. */
function Leaders({ sides }: { sides: LeaderSide[] }) {
  const t = useT();
  return (
    <div className="px-4 py-3">
      <div className="eyebrow mb-2.5 text-muted-foreground">{t("board.leaders")}</div>
      {/* ONE grid for BOTH sides. A grid per side resolved its own code
          column, so a four-letter code and a two-letter one started their
          names 16px apart (ODES 60px against SF's 44px floor, measured at
          375). Shared, the column is the widest code PRESENT: every name in
          the block starts at the same x, for any pair of codes, and no code
          is truncated to get there. Only the COLUMN axis is subgridded, so
          each level keeps its own row gap. */}
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-x-2.5 gap-y-3 text-sm">
        {sides.map((l) => (
          <div key={l.side} className="col-span-3 grid grid-cols-subgrid gap-y-1.5">
            {l.players.map(({ tally, player }, i) => (
              <div key={player.id} className="col-span-3 grid grid-cols-subgrid items-baseline">
                {i === 0 ? <MiniChip team={l.team} /> : <span />}
                <span className="min-w-0 text-pretty">{named(player)}</span>
                <span className="tabular font-semibold whitespace-nowrap text-muted-foreground">
                  {tally.goals}
                  {t("board.col.goals")}
                  {tally.assists > 0 && ` ${tally.assists}${t("board.col.assists")}`}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The numbers that do not need a chart, and what the game is worth.
 *
 * Each chip removes itself when it has nothing to say, so the row is never a
 * line of dashes. The stake is `groupStake`'s, which refuses to guess: it
 * claims the decider line only once every other game in the group is played.
 * A board that overstates what is on the line is worse than one that stays
 * quiet, and this stays quiet for scratch games, cross-division games and
 * anything mid-pool.
 */
interface FootFacts {
  chips: string[];
  stake: { label: string; group: string | undefined; ranks: string; tops: string | null } | null;
}

function footOf(
  state: GameState,
  teams: Team[],
  schedule: ScheduledGame[],
  pools: Pool[],
  divisions: Division[],
  t: ReturnType<typeof useT>,
): FootFacts | null {
  const points = state.points ?? [];
  const breaksA = breakCount(points, "A");
  const breaksB = breakCount(points, "B");
  const longest = longestPointMs(points);
  const last = pointLengthMs(points, points.length - 1);
  const target = state.config.scoreTarget;

  const chips: string[] = [];
  if (breaksA + breaksB > 0) {
    chips.push(
      `${t("board.breaks")} ${state.teamA.short} ${breaksA} · ${state.teamB.short} ${breaksB}`,
    );
  }
  if (last !== null) chips.push(`${t("board.lastPoint")} ${formatClock(last)}`);
  if (longest !== null) chips.push(`${t("board.longestPoint")} ${formatClock(longest)}`);
  if (target !== null) chips.push(t("board.gameTo", { n: target }));

  const stake = groupStake(
    teams,
    schedule,
    state.teamA.teamId,
    state.teamB.teamId,
    state.scheduleGameId ?? null,
  );
  if (chips.length === 0 && stake === null) return null;
  if (stake === null) return { chips, stake: null };

  // The table these ranks are in is a POOL when the teams are drawn into one
  // and the whole division otherwise, and the label has to say which - "5th"
  // means very different things in a group of four and a division of twenty.
  // The name itself is user content, so it is shown rather than composed into
  // a sentence that would read "place in pool Група A".
  const inPool = stake.poolId !== undefined;
  const group = inPool
    ? pools.find((p) => p.id === stake.poolId)?.name
    : divisions.find((d) => d.id === stake.divisionId)?.name;
  return {
    chips,
    stake: {
      label: t(inPool ? "board.placeInPool" : "board.placeInDivision"),
      group,
      ranks: `${state.teamA.short} ${ordinal(stake.rankA)} · ${state.teamB.short} ${ordinal(stake.rankB)}`,
      tops: stake.decidesTop ? t("board.winnerTops", { group: (group ?? "").toUpperCase() }) : null,
    },
  };
}

function Foot({ facts, odds }: { facts: FootFacts | null; odds: ReactNode }) {
  const { chips, stake } = facts ?? { chips: [], stake: null };
  return (
    <div className="flex flex-col gap-3 px-4 py-4">
      {odds}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <span
              key={c}
              // Tighter than `.eyebrow`: these carry a label AND a value, and
              // 0.3em of tracking pushed a four-chip row onto three lines at 375.
              className="rounded-md bg-card px-2 py-1.5 text-[0.625rem] font-medium tracking-wide text-muted-foreground"
            >
              {c}
            </span>
          ))}
        </div>
      )}
      {stake !== null && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-muted-foreground">
          <span className="eyebrow text-foreground">{stake.label}</span>
          <span>
            {stake.group !== undefined && <span className="font-semibold">{stake.group} · </span>}
            {stake.ranks}
          </span>
          {stake.tops !== null && (
            <span className="slant-chip ml-auto bg-brand px-2 py-1.5 text-brand-on">
              {stake.tops}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Win probability, as a bar that stays - on ONE line with its two numbers.
 *
 * The overlay flashes this for eight seconds after a score because a
 * broadcast frame is rented. This pane owns its space, so it can just be
 * there, which is better: the interesting moment is usually the one BEFORE a
 * point. It sits here rather than in the hero (2026-09-19) because it is the
 * one number on the screen that is MODELLED - everything in the hero is
 * observed - and this is where the other derived numbers already live.
 *
 * NEVER for a final: `winProbabilityA` answers 1 / 0 / 0.5 once the period is
 * "final", so an unguarded bar would print «100%» under every finished game.
 */
function WinBar({ state }: { state: GameState }) {
  const theme = useTheme();
  const t = useT();
  const p = winProbabilityA(state);
  if (p === null) return null;
  const pctA = displayPercent(p, false);
  const pctB = 100 - pctA;
  const trailing = pctA === pctB ? null : pctA > pctB ? "B" : "A";

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{t("board.winProbability")}</p>
      <div
        role="img"
        aria-label={`${t("board.winProbability")}: ${state.teamA.short} ${pctA}%, ${state.teamB.short} ${pctB}%`}
        className="tabular grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2.5 text-sm font-semibold"
      >
        <span className={cn(trailing === "A" && "text-muted-foreground")}>
          {state.teamA.short} {pctA}%
        </span>
        {/* Sheared, so the seam between the two fills IS a diagonal - the
          overlay's trick: a vertical edge inside a leaning box already is one,
          and no fill has to know where the split sits. */}
        <div className="lean relative h-2.5 overflow-hidden rounded-xs bg-foreground/10">
          <span
            data-edge={kitEdge(state.teamA.color, theme)}
            className="kit-edge absolute inset-y-0 left-0 origin-left"
            style={{
              width: "100%",
              transform: `scaleX(${pctA / 100})`,
              background: state.teamA.color,
            }}
          />
          <span
            data-edge={kitEdge(state.teamB.color, theme)}
            className="kit-edge absolute inset-y-0 right-0 origin-right"
            style={{
              width: "100%",
              transform: `scaleX(${pctB / 100})`,
              background: state.teamB.color,
            }}
          />
          {/* Dead centre: how far from even, without comparing two numbers. */}
          <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-background/60" />
        </div>
        <span className={cn(trailing === "B" && "text-muted-foreground")}>
          {pctB}% {state.teamB.short}
        </span>
      </div>
    </div>
  );
}
